import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import RegistrationFormContainer from "./RegistrationFormContainer";
import SiteRegistrationProps, { RegistrationType } from "./SiteRegistrationProps";
import RegistrationComplete from "./RegistrationComplete";
import { Typography } from "@kaltura/ds-react-components";
import { systemWidth } from "@kaltura/mediaspace-shared-styled";
import { ContentContext } from "./ContentContext";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { PostRegistration } from "../post-registration/PostRegistration";
import { useScript } from "@kaltura/mediaspace-shared-hooks";

const RegistrationContainer = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "fullWidth";
        },
    }
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    "&": fullWidth && systemWidth({ theme }),
    padding: fullWidth ? theme.spacing(0, 0, 7.5) : theme.spacing(4, 4, 11.5),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        padding: fullWidth ? theme.spacing(0, 0, 5) : theme.spacing(4, 4, 9),
    },
}));

const H1 = styled(Typography)(({ theme, variant }) => ({
    display: "block",
    marginTop: theme.spacing(7.5),
    marginBottom: theme.spacing(4),
    ...(
        variant === "h2" && {
            marginTop: 0,
            marginBottom: theme.spacing(3),
            textAlign: "center",
        }
    ),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginTop: theme.spacing(3),
    },
}));

const TitleText = styled(
    Typography,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "fullWidth";
        },
    }
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    marginBottom: fullWidth ? theme.spacing(7.5) : theme.spacing(1),
    display: "block",
    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
        ...(
            fullWidth && {
                width: "70%",
            }
        ),
    },
    [theme.breakpoints.up(theme.breakpoints.values.lg)]: {
        ...(
            fullWidth && {
                width: "60%",
            }
        ),
    },
}));

enum RegistrationFlowStep {
    REGISTRATION = "registration",
    POST_REGISTRATION = "post-registration",
    COMPLETED = "completed",
}

/**
 * Site Registration Main Component
 */
export function SiteRegistration({
    fullWidth = true,
    title,
    userData,
    sections,
    registrationType = RegistrationType.NEW,
    titleText,
    recaptchaSiteKey,
    recaptchaTheme,
    recapthcaMode,
    fieldsMappingToMarketo,
    marketoResponseTimeout,
    action,
    completeTitle,
    completeDescriptionHtml,
    completeButtonText,
    completeButtonUrl,
    onCompleteScriptContent,
    validateSsoDomains = false,
    registerOtherUser = false,
    onRegistrationCompleted,
    preSubmitScript,
}: SiteRegistrationProps) {
    const [registrationFlowStep, setRegistrationFlowStep] = useState<RegistrationFlowStep>(
        RegistrationFlowStep.REGISTRATION
    );
    const [postRegistrationActions, setPostRegistrationActions] = useState<KmsTypeComponent[]>();
    const [registrationCompletedRedirectUrl, setRegistrationCompletedRedirectUrl] = useState<string | undefined>();
    const { showToast } = useToastsContext();

    useEffect(() => {
        if (registerOtherUser) {
            showToast({
                severity: "info",
                message: translate("Please note that you are already logged in and there is no need to register"),
            });
        }
    }, [registerOtherUser, showToast]);

    // Use effect to handle redirect when registration flow completed
    useEffect(() => {
        if (registrationFlowStep === RegistrationFlowStep.COMPLETED && registrationCompletedRedirectUrl) {
            window.location.href = registrationCompletedRedirectUrl;
        }
    }, [registrationCompletedRedirectUrl, registrationFlowStep]);

    const handleRegistrationFlowCompleted = () => {
        setPostRegistrationActions(undefined);
        setRegistrationFlowStep(RegistrationFlowStep.COMPLETED);
    };

    const handleRegister = (redirectUrl?: string, postRegistrationActions?: KmsTypeComponent[]) => {
        if (postRegistrationActions && postRegistrationActions.length > 0) {
            setPostRegistrationActions(postRegistrationActions);
            setRegistrationFlowStep(RegistrationFlowStep.POST_REGISTRATION);
            onRegistrationCompleted?.();
        }
        else {
            handleRegistrationFlowCompleted();
            onRegistrationCompleted?.();
        }
        setRegistrationCompletedRedirectUrl(redirectUrl);
    };

    const showRegistrationFormContainer =
        registrationFlowStep === RegistrationFlowStep.REGISTRATION
        || registrationFlowStep === RegistrationFlowStep.COMPLETED
        || (registrationFlowStep === RegistrationFlowStep.POST_REGISTRATION && fullWidth);

    useScript(preSubmitScript);
    return (
        <ContentContext.Provider value={{ fullWidth: fullWidth }}>
            {showRegistrationFormContainer && (
                <RegistrationContainer className={"kms-ds-site-registration"} fullWidth={fullWidth}>
                    {registrationFlowStep === RegistrationFlowStep.COMPLETED && (
                        <RegistrationComplete
                            registrationType={registrationType}
                            completeTitle={completeTitle}
                            completeDescriptionHtml={completeDescriptionHtml}
                            completeButtonText={completeButtonText}
                            completeButtonUrl={completeButtonUrl}
                            onCompleteScriptContent={onCompleteScriptContent}
                        />
                    )}
                    {/*in fullWidth post registration shown in modal, we want to show it on top of registration form */}
                    {(
                        registrationFlowStep === RegistrationFlowStep.REGISTRATION
                        || (registrationFlowStep === RegistrationFlowStep.POST_REGISTRATION && fullWidth)
                    ) && (
                        <>
                            <H1
                                variant={fullWidth ? "h1" : "h2"}
                                component={"h1"}
                                className={"kms-ds-site-registration-title"}
                            >
                                {title}
                            </H1>
                            <TitleText
                                fullWidth={fullWidth}
                                variant={"body1"}
                                className={"kms-ds-site-registration--title-text"}
                            >
                                {HtmlReactParser(titleText)}
                            </TitleText>
                            <RegistrationFormContainer
                                userData={userData}
                                sections={sections}
                                recaptchaSiteKey={recaptchaSiteKey}
                                recaptchaTheme={recaptchaTheme}
                                recapthcaMode={recapthcaMode}
                                action={action}
                                registrationType={registrationType}
                                onRegister={handleRegister}
                                fieldsMappingToMarketo={fieldsMappingToMarketo}
                                marketoResponseTimeout={marketoResponseTimeout}
                                validateSsoDomains={validateSsoDomains}
                                registerOtherUser={registerOtherUser}
                            />
                        </>
                    )}
                </RegistrationContainer>
            )}
            {registrationFlowStep === RegistrationFlowStep.POST_REGISTRATION && postRegistrationActions && (
                <PostRegistration
                    openInModal={fullWidth}
                    postRegistrationSteps={postRegistrationActions}
                    onPostRegistrationCompleted={handleRegistrationFlowCompleted}
                />
            )}
        </ContentContext.Provider>
    );
}

export default SiteRegistration;
